import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { ChevronLeft, MapPin, Settings2, X } from "lucide-react";

import { api, RateLimitError } from "../api/client";
import { authenticated, login } from "../auth/keycloak";
import { AdminZoneCard } from "../components/AdminZoneCard";
import { IpccCard, selectIpccPoint, selectIpccZone } from "../components/IpccCard";
import {
  ClassificationCard, ForestStandCard, LandUseChangeCard, isForest,
} from "../components/LandUseCard";
import { LayerTable } from "../components/LayerTable";
import { SoilTextureCard } from "../components/SoilTextureCard";
import { WeatherDailyPanel } from "../components/WeatherDailyPanel";
import { WeatherSeriesChart } from "../components/WeatherSeriesChart";
import { WeatherSeriesTable } from "../components/WeatherSeriesTable";
import { WorldMap } from "../components/WorldMap";
import { Dropdown } from "../components/ds";
import { REIMS_POINT, REIMS_POLYGON, REIMS_ZONE } from "../demo/reims";
import type {
  CanopyHeight, CatalogResponse, LayerStats, LayerValue,
  PointResponse, SoilSource, WeatherSource, ZoneResponse,
} from "../types/api";

type Coords = { lat: number; lon: number };
type Mode = "point" | "zone";
type Tab = "soil" | "weather" | "climate" | "landuse";
type Ctx = { setBanner: (b: { message: string; retryAfter: number } | null) => void };

const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: "soil", label: "Soil" },
  { value: "weather", label: "Weather" },
  { value: "climate", label: "Climate" },
  { value: "landuse", label: "Land use" },
];

const ADMIN_LEVELS = ["adm5", "adm4", "adm3", "adm2", "adm1", "adm0"] as const;
type AdminLevel = (typeof ADMIN_LEVELS)[number];

const SOIL_SOURCE_OPTIONS: ReadonlyArray<{ value: SoilSource; label: string }> = [
  { value: "soilgrids", label: "SoilGrids 2.0" },
  { value: "lucas",     label: "LUCAS / ESDAC" },
];

const WEATHER_SOURCE_OPTIONS: ReadonlyArray<{ value: WeatherSource; label: string }> = [
  { value: "terraclimate", label: "TerraClimate" },
  { value: "era5",         label: "ERA5-Land" },
];

type YearsPreset = "latest" | "last3" | "last5" | "all";
const YEARS_OPTIONS: ReadonlyArray<{ value: YearsPreset; label: string }> = [
  { value: "latest", label: "Latest year" },
  { value: "last3",  label: "Last 3 years" },
  { value: "last5",  label: "Last 5 years" },
  { value: "all",    label: "All available" },
];

/** Pick the N most-recent weather years from the catalog. */
function resolveYears(catalog: CatalogResponse | null, preset: YearsPreset): number[] {
  if (!catalog) return [];
  const all = Array.from(
    new Set(catalog.layers.filter((l) => l.domain === "weather").flatMap((l) => l.years)),
  ).sort((a, b) => b - a);
  if (preset === "latest") return all.slice(0, 1);
  if (preset === "last3")  return all.slice(0, 3);
  if (preset === "last5")  return all.slice(0, 5);
  return all;
}

export default function ExplorePage() {
  const { setBanner } = useOutletContext<Ctx>();
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [picked, setPicked] = useState<Coords | null>(null);
  const [mode, setMode] = useState<Mode>("point");
  const [yearsPreset, setYearsPreset] = useState<YearsPreset>("last3");
  const [withLevel, setWithLevel] = useState<AdminLevel>("adm5");
  const [point, setPoint] = useState<PointResponse | null>(null);
  const [zone, setZone] = useState<ZoneResponse | null>(null);
  const [loading, setLoading] = useState(false);
  // Holds the last error message + a kind tag so the drawer can show a
  // rate-limit-specific UI vs a generic "request failed". Cleared on each
  // new pick / mode switch.
  const [error, setError] = useState<{ kind: "ratelimit" | "other"; message: string; retryAfter?: number } | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(true);
  // Canopy height comes from /landuse/extras — slow upstream call, fetched
  // in parallel with the main combined endpoint so the page never blocks
  // waiting on it. Anon demo mode sets a synthetic value below.
  const [canopy, setCanopy] = useState<CanopyHeight | null>(null);
  const [canopyLoading, setCanopyLoading] = useState(false);

  // Forest-geometry controls. Three toggles, two semantics:
  //   - `showForests` is a server-side opt-in: when off, we send
  //     include_geometry=false on the landuse calls and skip the
  //     polygonisation entirely (saves 50 KB–2 MB and 100 ms+ per click).
  //   - `showStable` / `showRegrowth` are pure client-side render
  //     toggles; the geometry is already on the wire when showForests
  //     is on. Off by default for the master, both on by default when
  //     the master flips on.
  const [showForests, setShowForests] = useState(false);
  const [showStable, setShowStable] = useState(true);
  const [showRegrowth, setShowRegrowth] = useState(true);

  // Soil provider — SoilGrids (global) by default, LUCAS (Europe) opt-in.
  // Auto-hide the LUCAS toggle when the catalog reports zero LUCAS rows so
  // operators who haven't ingested the bundle don't see a non-functional
  // switch.
  const [soilSource, setSoilSource] = useState<SoilSource>("soilgrids");

  // Weather data source — TerraClimate (annual, global) by default,
  // ERA5-Land opt-in when the ERA5 bundle is ingested.
  const [weatherSource, setWeatherSource] = useState<WeatherSource>("terraclimate");

  // Mobile control bar — collapsed by default on phones. Tapping the
  // settings chip expands the full bar. Desktop ignores this state.
  const [controlsOpen, setControlsOpen] = useState(false);
  const hasLucas = useMemo(
    () => (catalog?.layers ?? []).some((l) => l.domain === "soil" && l.source === "lucas"),
    [catalog],
  );

  // Active tab in URL — survives a re-pick and deep-links.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: Tab = (TABS.find((t) => t.value === tabParam)?.value) ?? "soil";
  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    api.catalog().then(setCatalog).catch(() => {});
  }, []);

  const yearList = useMemo(() => resolveYears(catalog, yearsPreset), [catalog, yearsPreset]);

  // Authentication gates the "real" API. Anonymous visitors get a baked
  // Reims sample on every click so they can explore the UI before signing
  // up — see /docs banner inside the drawer.
  const isAnon = !authenticated();

  useEffect(() => {
    if (!picked) return;
    // Clear stale results immediately so the map polygon disappears the
    // moment a new query starts (not only when the new response arrives).
    setPoint(null);
    setZone(null);
    setError(null);
    setCanopy(null);
    setCanopyLoading(false);
    setLoading(true);
    setDrawerOpen(true);

    if (isAnon) {
      // Demo mode — short delay makes the loading state perceptible so the
      // user understands a "request" is being simulated (and the swap to
      // Reims data isn't jarring).
      const t = setTimeout(() => {
        if (mode === "point") setPoint(REIMS_POINT);
        else                  setZone(REIMS_ZONE);
        // Reims is open cropland — no canopy. Surface a "no data" state so
        // the Forest stand card still demonstrates its empty-state UI.
        setCanopy({ mean_m: null, max_m: null, sample_pixels: 0,
          source: "Meta 1 m CHM (demo — open cropland)" });
        setLoading(false);
      }, 300);
      return () => clearTimeout(t);
    }

    const isSingle = yearList.length <= 1;
    const opts = isSingle
      ? { year: yearList[0], withLevel, includeGeometry: showForests, source: soilSource, weatherSource }
      : { years: yearList, withLevel, includeGeometry: showForests, source: soilSource, weatherSource };
    const fn = mode === "point"
      ? api.combinedPoint(picked.lat, picked.lon, opts).then(setPoint)
      : api.combinedZone(picked.lat, picked.lon, opts).then(setZone);
    fn.catch((e) => {
      if (e instanceof RateLimitError) {
        setBanner({ message: e.message, retryAfter: e.retryAfter });
        setError({ kind: "ratelimit", message: e.message, retryAfter: e.retryAfter });
      } else {
        setError({ kind: "other", message: (e as Error)?.message ?? "Request failed" });
      }
    }).finally(() => setLoading(false));

    // Slow upstream proxy — fire in parallel and let the Forest stand card
    // render with its own loading state. Errors here intentionally don't
    // surface a banner: Meta CHM gaps are the common case and they're not
    // a "failure" the user should worry about.
    setCanopyLoading(true);
    api.landuse.extras(picked.lat, picked.lon)
      .then((r) => setCanopy(r.canopy_height))
      .catch(() => setCanopy(null))
      .finally(() => setCanopyLoading(false));
  }, [picked, mode, yearList, withLevel, setBanner, isAnon, showForests, soilSource, weatherSource]);

  // Map polygon overlay — only when we're in zonal mode and have a resolved
  // admin zone. Switching back to point, picking elsewhere, or unsuccessful
  // resolution all drop the overlay (zone is cleared above on every new pick).
  const mapZone = mode === "zone" && zone?.admin?.gid != null
    ? { gid: zone.admin.gid, level: zone.admin.level }
    : null;

  // Forest geometry — split into stable (Hansen) + regrowth (WorldCover)
  // layers. Each is gated by its own client-side visibility toggle, AND
  // by the master `showForests` opt-in (without which the backend returns
  // null for both fields). Anon demo mode skips both — REIMS_POINT/ZONE
  // don't carry baked geometry.
  const stableGeometry = (!isAnon && showForests && showStable)
    ? (mode === "point"
        ? (point?.forest_patch?.geometry ?? null)
        : (zone?.forest_patches?.geometry ?? null))
    : null;
  const regrowthGeometry = (!isAnon && showForests && showRegrowth)
    ? (mode === "point"
        ? (point?.regrowth_patch?.geometry ?? null)
        : (zone?.regrowth_patches?.geometry ?? null))
    : null;

  const result = point ?? zone;
  const weatherSeries = result?.weather_series ?? null;
  const yearsChip =
    yearList.length === 0
      ? null
      : yearList.length === 1
        ? `${yearList[0]}`
        : `${yearList[yearList.length - 1]}–${yearList[0]} · ${yearList.length}y`;

  return (
    <div className="relative flex-1 min-h-0">
      <WorldMap
        picked={picked}
        onPick={setPicked}
        zone={mapZone}
        polygonOverride={isAnon && mapZone ? REIMS_POLYGON : undefined}
        stableGeometry={stableGeometry}
        regrowthGeometry={regrowthGeometry}
      />

      {isAnon && (
        <div className="absolute left-3 right-3 top-3 z-[1100] sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:max-w-xl rounded-xs border-[1.2px] border-mef-orange/40 bg-amber-50/95 backdrop-blur px-4 py-2 shadow-mef-popup pointer-events-auto md:top-3 mt-12 md:mt-0">
          <div className="text-[11px] uppercase tracking-[0.04em] font-medium text-mef-orange mb-0.5">
            Demo mode
          </div>
          <div className="text-[12px] leading-5 text-black/70">
            You're seeing a baked sample for <strong>Reims, France</strong> regardless of where you click.{" "}
            <button onClick={() => login()} className="font-medium text-mef-green underline underline-offset-2 hover:text-mef-orange mef-focus-ring">
              Sign in
            </button>{" "}
            (Google or email) to query real coordinates — the free tier covers 2 000 daily units
            (≈ 200 point lookups).
          </div>
        </div>
      )}

      {/* Floating control bar — pinned over the top-left of the map.
          Below md (768) it collapses to a single Settings chip the user
          taps to expand a sheet. The mobile drawer is right-edge-aligned
          and full width so it doesn't shove the satellite picker around. */}
      <button
        type="button"
        onClick={() => setControlsOpen((v) => !v)}
        className={[
          "md:hidden absolute left-3 top-3 z-[1001] inline-flex items-center gap-1.5 h-9 px-3 rounded-xs",
          "border-[1.2px] border-mef-separator bg-white/95 backdrop-blur shadow-mef-popup",
          "text-[11px] uppercase tracking-[0.04em] font-medium text-black/70 mef-focus-ring",
        ].join(" ")}
        aria-expanded={controlsOpen}
        aria-label="Toggle map controls"
      >
        {controlsOpen ? <X className="h-4 w-4" /> : <Settings2 className="h-4 w-4" />}
        <span>{controlsOpen ? "Close" : (mode === "point" ? "Point" : "Zone")}</span>
      </button>

      <div
        className={[
          "absolute z-[1000] flex flex-wrap items-end gap-3 rounded-xs border-[1.2px] border-mef-separator bg-white/95 backdrop-blur px-3 py-2 shadow-mef-popup",
          // Desktop: classic top-left layout
          "md:left-3 md:top-3 md:max-w-none md:flex",
          // Mobile: collapsible sheet anchored below the chip
          controlsOpen
            ? "left-3 right-3 top-14 max-h-[calc(100vh-12rem)] overflow-y-auto"
            : "hidden md:flex",
        ].join(" ")}
      >
        <div>
          <div className="mef-small-caption mb-1">Mode</div>
          <div className="inline-flex bg-white border-[1.2px] border-mef-gray rounded-xs overflow-hidden">
            {(["point", "zone"] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={[
                  "px-3 h-8 text-[11px] font-medium uppercase tracking-[0.04em] transition-colors mef-focus-ring",
                  mode === m
                    ? "bg-mef-green text-white"
                    : "text-black/60 hover:bg-mef-surface-2",
                ].join(" ")}
              >
                {m === "point" ? "Point" : "Zone"}
              </button>
            ))}
          </div>
        </div>

        <div className="min-w-[130px]">
          <div className="mef-small-caption mb-1">Admin cap</div>
          <Dropdown<AdminLevel>
            value={withLevel}
            options={ADMIN_LEVELS}
            onChange={setWithLevel}
            minWidth={130}
          />
        </div>

        <div className="min-w-[170px]">
          <div className="mef-small-caption mb-1">Weather years</div>
          <Dropdown<YearsPreset>
            value={yearsPreset}
            options={YEARS_OPTIONS}
            onChange={setYearsPreset}
            minWidth={170}
          />
        </div>

        <div className="min-w-[150px]">
          <div className="mef-small-caption mb-1">Weather source</div>
          <Dropdown<WeatherSource>
            value={weatherSource}
            options={WEATHER_SOURCE_OPTIONS}
            onChange={setWeatherSource}
            minWidth={150}
          />
        </div>

        {hasLucas && (
          <div className="min-w-[160px]">
            <div className="mef-small-caption mb-1">Soil source</div>
            <Dropdown<SoilSource>
              value={soilSource}
              options={SOIL_SOURCE_OPTIONS}
              onChange={setSoilSource}
              minWidth={160}
            />
          </div>
        )}

        {yearsChip && (
          <div className="h-8 hidden md:flex items-center">
            <span className="text-[11px] uppercase tracking-[0.04em] text-black/40">
              {yearsChip}
            </span>
          </div>
        )}

        {/* Forests — master toggle gates the heavy polygonisation server-side,
            sub-toggles control which layer renders. Stacked vertically so they
            fit alongside the horizontal Mode/Admin/Years column without
            blowing out the floating bar's footprint. */}
        <div className="flex flex-col gap-1 md:pl-3 md:ml-1 md:border-l md:border-mef-separator pt-2 md:pt-0 border-t md:border-t-0 border-mef-separator w-full md:w-auto">
          <label className="flex items-center gap-2 text-[11px] uppercase tracking-[0.04em] text-black/60 cursor-pointer mef-focus-ring">
            <input
              type="checkbox"
              checked={showForests}
              onChange={(e) => setShowForests(e.target.checked)}
              className="h-3.5 w-3.5 accent-mef-green"
            />
            Forests
          </label>
          <label className={[
            "flex items-center gap-2 text-[11px] ml-4",
            showForests ? "text-black/70 cursor-pointer" : "text-black/30 cursor-not-allowed",
          ].join(" ")}>
            <input
              type="checkbox"
              checked={showStable}
              disabled={!showForests}
              onChange={(e) => setShowStable(e.target.checked)}
              className="h-3 w-3 accent-mef-green"
            />
            <span className="inline-block h-2 w-2 rounded-xs" style={{ backgroundColor: "#388e3c" }} />
            Stable
          </label>
          <label className={[
            "flex items-center gap-2 text-[11px] ml-4",
            showForests ? "text-black/70 cursor-pointer" : "text-black/30 cursor-not-allowed",
          ].join(" ")}>
            <input
              type="checkbox"
              checked={showRegrowth}
              disabled={!showForests}
              onChange={(e) => setShowRegrowth(e.target.checked)}
              className="h-3 w-3 accent-mef-green"
            />
            <span className="inline-block h-2 w-2 rounded-xs" style={{ backgroundColor: "#9ccc65" }} />
            Regrowth
          </label>
        </div>
      </div>

      {/* Data drawer */}
      {drawerOpen ? (
        <aside className="absolute right-0 top-0 bottom-0 z-[900] flex w-full sm:w-[480px] flex-col bg-white border-l-[1.2px] border-mef-separator shadow-mef-popup">
          <header className="shrink-0 flex items-center justify-between gap-2 border-b border-mef-separator bg-mef-subtle px-4 py-2.5">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.04em] text-black/60 min-w-0">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-mef-orange" />
              {picked ? (
                <span className="font-mono tabular-nums text-black/80 truncate">
                  {picked.lat.toFixed(4)}°, {picked.lon.toFixed(4)}°
                </span>
              ) : (
                <span>Click the map to query</span>
              )}
            </div>
            <button
              onClick={() => setDrawerOpen(false)}
              className="h-7 w-7 inline-flex items-center justify-center rounded-xs text-black/60 hover:bg-mef-surface-2 mef-focus-ring"
              aria-label="Close panel"
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          <nav className="shrink-0 flex gap-0 border-b border-mef-separator px-2">
            {TABS.map((t) => (
              <button
                key={t.value}
                onClick={() => setTab(t.value)}
                className={[
                  "px-3 h-9 -mb-px border-b-2 text-[12px] font-medium transition-colors mef-focus-ring",
                  tab === t.value
                    ? "border-mef-green text-black/80"
                    : "border-transparent text-black/50 hover:text-black/80",
                ].join(" ")}
              >
                {t.label}
              </button>
            ))}
          </nav>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {!picked ? (
              <div className="rounded-xs border-[1.2px] border-dashed border-mef-gray bg-mef-subtle p-8 text-center text-[13px] leading-4 text-black/60">
                Click anywhere on the map to query soil &amp; weather here.
              </div>
            ) : loading ? (
              <div className="rounded-xs border-[1.2px] border-mef-separator bg-white p-8 text-center text-[13px] text-black/60 shadow-mef-card">
                Loading…
              </div>
            ) : error ? (
              <div className={[
                "rounded-xs border-[1.2px] p-6 text-[13px] leading-5 shadow-mef-card",
                error.kind === "ratelimit"
                  ? "border-mef-orange/40 bg-amber-50 text-amber-900"
                  : "border-red-300 bg-red-50 text-red-900",
              ].join(" ")}>
                <div className="text-[11px] uppercase tracking-[0.04em] font-medium mb-1">
                  {error.kind === "ratelimit" ? "Rate limit reached" : "Request failed"}
                </div>
                <div>{error.message}</div>
                {error.kind === "ratelimit" && error.retryAfter ? (
                  <div className="mt-2 text-[12px] text-amber-800">
                    Try again in ~{error.retryAfter}s, or upgrade your plan for a higher cap.
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <AdminZoneCard zone={point?.admin ?? zone?.admin ?? null} />

                {tab === "soil" && (
                  <>
                    <LayerTable
                      title="Soil"
                      rows={
                        mode === "point"
                          ? (point?.soil ?? []).filter((r: LayerValue) => r.label == null)
                          : (zone?.soil ?? []).filter((r: LayerStats) => r.mode_label == null)
                      }
                    />
                    <SoilTextureCard
                      schemes={catalog?.soil_texture_schemes ?? []}
                      classifications={point?.soil_texture ?? zone?.soil_texture ?? null}
                      soil={mode === "point" ? point?.soil : zone?.soil}
                      columns={2}
                    />
                  </>
                )}

                {tab === "weather" && (
                  <>
                    {weatherSeries && weatherSeries.length > 0 ? (
                      <>
                        <WeatherSeriesChart rows={weatherSeries} columns={1} />
                        <WeatherSeriesTable rows={weatherSeries} />
                      </>
                    ) : (
                      <LayerTable title="Weather" rows={point?.weather ?? zone?.weather ?? []} />
                    )}
                    {weatherSource === "era5" && (
                      <WeatherDailyPanel lat={picked?.lat ?? null} lon={picked?.lon ?? null} />
                    )}
                  </>
                )}

                {tab === "climate" && (
                  mode === "point" ? (
                    <IpccCard items={selectIpccPoint(point?.climate, point?.soil)} />
                  ) : (
                    <IpccCard items={selectIpccZone(zone?.climate, zone?.soil)} modal />
                  )
                )}

                {tab === "landuse" && (() => {
                  const landuseRows = mode === "point"
                    ? (point?.landuse ?? [])
                    : (zone?.landuse ?? []);
                  const fc = mode === "zone" ? (zone?.forest_change ?? null) : null;
                  if (landuseRows.length === 0 && !fc) {
                    return (
                      <div className="rounded-xs border-[1.2px] border-mef-separator bg-white p-4 text-[13px] text-black/40 shadow-mef-card">
                        Land-use layers haven't been baked for this region yet.
                        Run <code className="font-mono text-[12px]">update_landuse</code> on the backend.
                      </div>
                    );
                  }
                  return (
                    <>
                      <ClassificationCard rows={landuseRows} modal={mode === "zone"} />
                      <LandUseChangeCard rows={landuseRows} forestChange={fc} mode={mode} />
                      {isForest(landuseRows) && (
                        <ForestStandCard
                          rows={landuseRows}
                          canopy={canopy}
                          canopyLoading={canopyLoading}
                        />
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </aside>
      ) : (
        <button
          onClick={() => setDrawerOpen(true)}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-[900] flex h-14 w-7 items-center justify-center rounded-l-xs border-[1.2px] border-r-0 border-mef-separator bg-white shadow-mef-card hover:bg-mef-subtle mef-focus-ring"
          aria-label="Open data panel"
        >
          <ChevronLeft className="h-4 w-4 text-black/60" />
        </button>
      )}
    </div>
  );
}
